
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
    ArrowLeft,
    ArrowRight,
    CheckCircle2,
    Eye,
    EyeOff,
    Hotel,
    LockKeyhole,
    Mail,
    ShieldCheck,
    Sparkles,
    UserRound,
    X,
} from "lucide-react";

const API = "https://dubaibudgetivehotels.com/api";

const DUBAI_IMAGE =
    "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?auto=format&fit=crop&w=1200&q=85";

const inputClass =
    "w-full rounded-xl border border-[#DCEAE1] bg-[#F8FBF9] py-3.5 pl-12 pr-12 text-sm text-[#17324D] outline-none transition placeholder:text-[#9AA8B5] focus:border-[#168B43] focus:bg-white focus:ring-4 focus:ring-[#168B43]/10";

const labelClass =
    "mb-2 block text-[12px] font-extrabold text-[#17324D]";

export default function LoginModal({
    isOpen,
    onClose,
    setUser,
}) {
    const navigate = useNavigate();

    const [mode, setMode] = useState("login");
    const [form, setForm] = useState({
        name: "",
        email: "",
        password: "",
    });

    const [errors, setErrors] = useState({});
    const [success, setSuccess] = useState("");
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    /* =====================================================
       CLOSE WITH ESCAPE
    ===================================================== */

    useEffect(() => {
        if (!isOpen) return;

        const handleEscape = (event) => {
            if (event.key === "Escape" && !loading) {
                onClose?.();
            }
        };

        window.addEventListener("keydown", handleEscape);

        return () => {
            window.removeEventListener("keydown", handleEscape);
        };
    }, [isOpen, loading, onClose]);

    /* =====================================================
       FORM HELPERS
    ===================================================== */

    const handleChange = (event) => {
        const { name, value } = event.target;

        setForm((previous) => ({
            ...previous,
            [name]: value,
        }));

        setErrors((previous) => ({
            ...previous,
            [name]: "",
            general: "",
        }));

        setSuccess("");
    };

    const switchMode = (nextMode) => {
        if (loading) return;

        setMode(nextMode);
        setErrors({});
        setSuccess("");
        setShowPassword(false);

        setForm((previous) => ({
            name: nextMode === "register" ? previous.name : "",
            email: previous.email,
            password: "",
        }));
    };

    const validate = () => {
        const nextErrors = {};

        if (mode === "register" && !form.name.trim()) {
            nextErrors.name = "Please enter your full name.";
        }

        if (!form.email.trim()) {
            nextErrors.email = "Please enter your email address.";
        } else if (
            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())
        ) {
            nextErrors.email = "Please enter a valid email address.";
        }

        if (mode !== "forgot" && !form.password.trim()) {
            nextErrors.password = "Please enter your password.";
        }

        return nextErrors;
    };

    /* =====================================================
       LOGIN / REGISTER / FORGOT PASSWORD
    ===================================================== */

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (loading) return;

        setErrors({});
        setSuccess("");

        const validationErrors = validate();

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setLoading(true);

        try {
            /* FORGOT PASSWORD */
            if (mode === "forgot") {
                await axios.post(`${API}/forgot-password`, {
                    email: form.email.trim(),
                });

                setSuccess(
                    "If an account exists with this email, a password reset link has been sent."
                );

                return;
            }

            /* REGISTER */
            if (mode === "register") {
                await axios.post(`${API}/register`, {
                    name: form.name.trim(),
                    email: form.email.trim(),
                    password: form.password,
                    password_confirmation: form.password,
                });

                setForm({
                    name: "",
                    email: form.email.trim(),
                    password: "",
                });

                setMode("login");

                setSuccess(
                    "Your account has been created successfully. You can now sign in."
                );

                return;
            }

            /* LOGIN */
            const response = await axios.post(`${API}/login`, {
                email: form.email.trim(),
                password: form.password,
            });

            const userData = response.data.user;

            if (!userData) {
                throw new Error("The login response did not contain user details.");
            }

            localStorage.setItem("user", JSON.stringify(userData));

            setUser(userData);

            setForm({
                name: "",
                email: "",
                password: "",
            });

            setMode("login");
            setErrors({});
            setSuccess("");

            onClose?.();

            navigate("/profile");
        } catch (error) {
            console.error("Authentication error:", error);

            const apiErrors = error.response?.data?.errors;
            const fieldErrors = {};

            if (apiErrors && typeof apiErrors === "object") {
                Object.entries(apiErrors).forEach(([key, value]) => {
                    if (["name", "email", "password"].includes(key)) {
                        fieldErrors[key] = Array.isArray(value)
                            ? value[0]
                            : String(value);
                    }
                });
            }

            setErrors({
                ...fieldErrors,
                general:
                    error.response?.data?.message ||
                    error.message ||
                    "Something went wrong. Please try again.",
            });
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    const isLogin = mode === "login";
    const isRegister = mode === "register";
    const isForgot = mode === "forgot";

    const heading = isRegister
        ? "Create Your Account."
        : isForgot
            ? "Reset Your Password."
            : "Welcome Back.";

    const description = isRegister
        ? "Join us to manage your Dubai hotel reservations and travel plans in one place."
        : isForgot
            ? "Enter your registered email address and we'll help you recover access to your account."
            : "Sign in to access your saved hotels, upcoming stays and personal travel dashboard.";

    /* =====================================================
       RENDER
    ===================================================== */

    return (
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center overflow-y-auto bg-[#032F28]/85 px-3 py-5 backdrop-blur-md sm:px-5"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget && !loading) {
                    onClose?.();
                }
            }}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="login-modal-title"
                className="relative my-auto grid w-full max-w-[1000px] overflow-hidden rounded-[24px] bg-white shadow-[0_35px_100px_rgba(0,0,0,0.30)] lg:grid-cols-[43%_57%]"
            >
                {/* =================================================
            LEFT SIDE - HOTEL VISUAL
        ================================================= */}

                <div className="relative hidden min-h-[610px] overflow-hidden bg-[#064E3B] lg:block">
                    <img
                        src={DUBAI_IMAGE}
                        alt="Dubai skyline"
                        className="absolute inset-0 h-full w-full object-cover"
                    />

                    <div className="absolute inset-0 bg-gradient-to-t from-[#032F28]/95 via-[#064E3B]/75 to-[#064E3B]/30" />

                    <div className="relative flex h-full flex-col justify-between p-9">
                        {/* BRAND */}
                        <div className="flex items-center gap-3">
                            <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/25 bg-white/15 text-white backdrop-blur-sm">
                                <Hotel size={23} />
                            </div>

                            <div>
                                <p className="text-[17px] font-extrabold leading-tight text-white">
                                    Dubai Budgetive Hotels
                                </p>

                                <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#B6F2CB]">
                                    Your Dubai Stay Starts Here
                                </p>
                            </div>
                        </div>

                        {/* LEFT CONTENT */}
                        <div>
                            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-2 text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#B6F2CB] backdrop-blur-sm">
                                <Sparkles size={15} />
                                Discover Dubai
                            </div>

                            <h2 className="max-w-[360px] text-[38px] font-extrabold leading-[1.15] tracking-tight text-white">
                                Your Perfect
                                <br />
                                Dubai Stay
                                <br />
                                <span className="text-[#A5E8B8]">
                                    Starts Here.
                                </span>
                            </h2>

                            <p className="mt-5 max-w-[330px] text-sm leading-8 text-white/80">
                                Discover hotels, save your favorites and
                                manage every booking from your personal account.
                            </p>

                            <div className="mt-8 space-y-4">
                                {[
                                    "Manage your hotel reservations",
                                    "Keep your favorite hotels together",
                                    "Share your hotel experiences",
                                ].map((feature) => (
                                    <div
                                        key={feature}
                                        className="flex items-center gap-3 text-[13px] font-semibold text-white/90"
                                    >
                                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/15 text-[#B6F2CB]">
                                            <CheckCircle2 size={16} />
                                        </span>

                                        {feature}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* BOTTOM BRAND */}
                        <div className="flex items-center gap-2 border-t border-white/20 pt-5 text-xs font-semibold text-white/70">
                            <ShieldCheck size={17} className="text-[#B6F2CB]" />
                            Your travel plans, in one convenient place.
                        </div>
                    </div>
                </div>

                {/* =================================================
            RIGHT SIDE - FORM
        ================================================= */}

                <div className="relative max-h-[90vh] overflow-y-auto bg-white">
                    {/* CLOSE BUTTON */}
                    <button
                        type="button"
                        onClick={() => {
                            if (!loading) onClose?.();
                        }}
                        disabled={loading}
                        aria-label="Close authentication modal"
                        className="absolute right-5 top-5 z-20 flex h-10 w-10 items-center justify-center rounded-full border border-[#E1ECE5] bg-[#F8FBF9] text-[#718096] transition hover:border-[#168B43] hover:bg-[#E8F7EE] hover:text-[#168B43] disabled:opacity-50"
                    >
                        <X size={19} />
                    </button>

                    <div className="px-6 pb-8 pt-9 sm:px-10 sm:pb-10 sm:pt-11 lg:px-12">
                        {/* MOBILE BRAND */}
                        <div className="mb-7 flex items-center gap-3 lg:hidden">
                            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#E8F7EE] text-[#168B43]">
                                <Hotel size={23} />
                            </div>

                            <div>
                                <p className="text-[16px] font-extrabold text-[#17324D]">
                                    Dubai Budgetive Hotels
                                </p>

                                <p className="text-[10px] font-semibold text-[#718096]">
                                    Your Dubai Stay Starts Here
                                </p>
                            </div>
                        </div>

                        {/* BACK BUTTON FOR FORGOT PASSWORD */}
                        {isForgot && (
                            <button
                                type="button"
                                onClick={() => switchMode("login")}
                                className="mb-6 inline-flex items-center gap-2 text-xs font-extrabold text-[#168B43] transition hover:gap-3"
                            >
                                <ArrowLeft size={16} />
                                Back to Sign In
                            </button>
                        )}

                        {/* HEADING ICON */}
                        <div className="flex h-13 w-13 items-center justify-center rounded-2xl bg-[#E8F7EE] p-3 text-[#168B43]">
                            {isRegister ? (
                                <UserRound size={25} />
                            ) : isForgot ? (
                                <Mail size={25} />
                            ) : (
                                <LockKeyhole size={25} />
                            )}
                        </div>

                        <p className="mt-5 text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#168B43]">
                            {isRegister
                                ? "Join Our Community"
                                : isForgot
                                    ? "Account Recovery"
                                    : "Your Personal Account"}
                        </p>

                        <h2
                            id="login-modal-title"
                            className="mt-2 text-[30px] font-extrabold leading-tight tracking-tight text-[#17324D] sm:text-[36px]"
                        >
                            {heading}
                        </h2>

                        <p className="mt-3 max-w-[440px] text-[13px] leading-7 text-[#718096]">
                            {description}
                        </p>

                        {/* =================================================
                LOGIN / REGISTER TABS
            ================================================= */}

                        {!isForgot && (
                            <div
                                className="mt-7 grid grid-cols-2 gap-1.5 rounded-xl bg-[#F0F6F2] p-1.5"
                                role="group"
                                aria-label="Authentication options"
                            >
                                <button
                                    type="button"
                                    onClick={() => switchMode("login")}
                                    disabled={loading}
                                    className={`rounded-lg px-4 py-3 text-[13px] font-extrabold transition ${isLogin
                                        ? "bg-white text-[#168B43] shadow-sm"
                                        : "text-[#718096] hover:text-[#17324D]"
                                        }`}
                                >
                                    Sign In
                                </button>

                                <button
                                    type="button"
                                    onClick={() => switchMode("register")}
                                    disabled={loading}
                                    className={`rounded-lg px-4 py-3 text-[13px] font-extrabold transition ${isRegister
                                        ? "bg-white text-[#168B43] shadow-sm"
                                        : "text-[#718096] hover:text-[#17324D]"
                                        }`}
                                >
                                    Create Account
                                </button>
                            </div>
                        )}

                        {/* =================================================
                MESSAGES
            ================================================= */}

                        {errors.general && (
                            <div
                                role="alert"
                                className="mt-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-xs leading-6 text-red-700"
                            >
                                {errors.general}
                            </div>
                        )}

                        {success && (
                            <div
                                role="status"
                                className="mt-5 flex items-start gap-3 rounded-xl border border-[#B9E4C6] bg-[#E8F7EE] px-4 py-3 text-xs leading-6 text-[#087D37]"
                            >
                                <CheckCircle2
                                    size={18}
                                    className="mt-0.5 shrink-0"
                                />
                                {success}
                            </div>
                        )}

                        {/* =================================================
                AUTH FORM
            ================================================= */}

                        <form
                            onSubmit={handleSubmit}
                            noValidate
                            className="mt-7 space-y-5"
                        >
                            {/* FULL NAME */}
                            {isRegister && (
                                <div>
                                    <label
                                        htmlFor="auth-name"
                                        className={labelClass}
                                    >
                                        Full Name
                                    </label>

                                    <div className="relative">
                                        <UserRound
                                            size={18}
                                            className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[#168B43]"
                                        />

                                        <input
                                            id="auth-name"
                                            type="text"
                                            name="name"
                                            autoComplete="name"
                                            value={form.name}
                                            onChange={handleChange}
                                            placeholder="Enter your full name"
                                            disabled={loading}
                                            aria-invalid={Boolean(errors.name)}
                                            className={inputClass}
                                        />
                                    </div>

                                    {errors.name && (
                                        <p className="mt-2 text-xs text-red-600">
                                            {errors.name}
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* EMAIL */}
                            <div>
                                <label
                                    htmlFor="auth-email"
                                    className={labelClass}
                                >
                                    Email Address
                                </label>

                                <div className="relative">
                                    <Mail
                                        size={18}
                                        className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[#168B43]"
                                    />

                                    <input
                                        id="auth-email"
                                        type="email"
                                        name="email"
                                        autoComplete="email"
                                        value={form.email}
                                        onChange={handleChange}
                                        placeholder="Enter your email address"
                                        disabled={loading}
                                        aria-invalid={Boolean(errors.email)}
                                        className={inputClass}
                                    />
                                </div>

                                {errors.email && (
                                    <p className="mt-2 text-xs text-red-600">
                                        {errors.email}
                                    </p>
                                )}
                            </div>

                            {/* PASSWORD */}
                            {!isForgot && (
                                <div>
                                    <div className="mb-2 flex items-center justify-between gap-3">
                                        <label
                                            htmlFor="auth-password"
                                            className="text-[12px] font-extrabold text-[#17324D]"
                                        >
                                            Password
                                        </label>

                                        {isLogin && (
                                            <button
                                                type="button"
                                                onClick={() => switchMode("forgot")}
                                                disabled={loading}
                                                className="text-[11px] font-extrabold text-[#168B43] hover:underline"
                                            >
                                                Forgot Password?
                                            </button>
                                        )}
                                    </div>

                                    <div className="relative">
                                        <LockKeyhole
                                            size={18}
                                            className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[#168B43]"
                                        />

                                        <input
                                            id="auth-password"
                                            type={showPassword ? "text" : "password"}
                                            name="password"
                                            autoComplete={
                                                isRegister
                                                    ? "new-password"
                                                    : "current-password"
                                            }
                                            value={form.password}
                                            onChange={handleChange}
                                            placeholder="Enter your password"
                                            disabled={loading}
                                            aria-invalid={Boolean(errors.password)}
                                            className={inputClass}
                                        />

                                        <button
                                            type="button"
                                            onClick={() => setShowPassword((value) => !value)}
                                            disabled={loading}
                                            aria-label={
                                                showPassword
                                                    ? "Hide password"
                                                    : "Show password"
                                            }
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-[#8DA29A] transition hover:text-[#168B43]"
                                        >
                                            {showPassword ? (
                                                <EyeOff size={19} />
                                            ) : (
                                                <Eye size={19} />
                                            )}
                                        </button>
                                    </div>

                                    {errors.password && (
                                        <p className="mt-2 text-xs text-red-600">
                                            {errors.password}
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* SUBMIT */}
                            <button
                                type="submit"
                                disabled={loading}
                                className="group flex w-full items-center justify-center gap-3 rounded-xl bg-[#168B43] px-6 py-4 text-[14px] font-extrabold text-white shadow-[0_10px_25px_rgba(22,139,67,0.15)] transition hover:-translate-y-0.5 hover:bg-[#087D37] disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {loading ? (
                                    <>
                                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                                        Please Wait...
                                    </>
                                ) : (
                                    <>
                                        {isRegister
                                            ? "Create My Account"
                                            : isForgot
                                                ? "Send Reset Link"
                                                : "Sign In To My Account"}

                                        <ArrowRight
                                            size={18}
                                            className="transition group-hover:translate-x-1"
                                        />
                                    </>
                                )}
                            </button>
                        </form>

                        {/* =================================================
                BOTTOM LINKS
            ================================================= */}

                        <div className="mt-6 text-center">
                            {isForgot ? (
                                <button
                                    type="button"
                                    onClick={() => switchMode("login")}
                                    className="inline-flex items-center gap-2 text-xs font-extrabold text-[#168B43] hover:underline"
                                >
                                    <ArrowLeft size={15} />
                                    Return To Sign In
                                </button>
                            ) : isLogin ? (
                                <p className="text-[13px] text-[#718096]">
                                    Don't have an account?{" "}
                                    <button
                                        type="button"
                                        onClick={() => switchMode("register")}
                                        className="font-extrabold text-[#168B43] hover:underline"
                                    >
                                        Create Account
                                    </button>
                                </p>
                            ) : (
                                <p className="text-[13px] text-[#718096]">
                                    Already have an account?{" "}
                                    <button
                                        type="button"
                                        onClick={() => switchMode("login")}
                                        className="font-extrabold text-[#168B43] hover:underline"
                                    >
                                        Sign In
                                    </button>
                                </p>
                            )}
                        </div>

                        {/* SECURITY FOOTER */}
                        <div className="mt-8 flex items-center justify-center gap-2 border-t border-[#E3ECE6] pt-6 text-[11px] font-semibold text-[#8DA29A]">
                            <ShieldCheck
                                size={17}
                                className="text-[#168B43]"
                            />
                            Secure Account Access
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
